import { useState, useCallback, useEffect, useRef } from 'react';
import type { ServerMessage } from '@roll1/shared';

interface BatchProgress {
  label: string;
  current: number;
  total: number;
}

/**
 * Hook for tracking batch operation progress.
 * Listens for state:batch-progress events and auto-hides on state:batch-update.
 */
export function useBatchProgress() {
  const [progress, setProgress] = useState<BatchProgress | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMessage = useCallback((msg: ServerMessage) => {
    if (msg.type === 'state:batch-progress') {
      const data = msg as ServerMessage & { label: string; current: number; total: number };
      setProgress({
        label: data.label,
        current: data.current,
        total: data.total,
      });
      // Clear any pending hide timer
      if (hideTimerRef.current !== null) {
        clearTimeout(hideTimerRef.current);
      }
    }

    if (msg.type === 'state:batch-update') {
      // Auto-hide progress after a short delay
      hideTimerRef.current = setTimeout(() => {
        setProgress(null);
      }, 1000);
    }
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hideTimerRef.current !== null) {
        clearTimeout(hideTimerRef.current);
      }
    };
  }, []);

  return {
    progress,
    handleMessage,
    isActive: progress !== null,
  };
}
